import React, { useState, useEffect, useRef } from 'react';
//import { Chapters, CourseContent, CourseHandsOn } from '../learn_components';
import '../styles/learn.css';
import Chapters from '../learn_components/Chapters';
import CourseContent from '../learn_components/CourseContent';
import CourseHandsOn from '../learn_components/CourseHandsOn';
import ChapterProject from '../learn_components/ChapterProject';
import BackgroundGenerationBanner from '../components/BackgroundGenerationBanner';
import GenerationStatusIndicator from '../components/GenerationStatusIndicator';
import { getCourseWithContent } from '../context/engine.ts';
import { supabase } from '../integrations/client.ts';

function Learn({ 
    isAuthenticated, 
    currentUser, 
    currentCourse, 
    onCourseSelect, 
    onNavigateToInquiry,
    onNavigateToLanding,
    theme,
    onToggleTheme
}) {
    const [chaptersState, setChaptersState] = useState('hidden'); // 'hidden', 'entering', 'visible', 'exiting'
    const [handsOnVisible, setHandsOnVisible] = useState(false);
    const [selectedSection, setSelectedSection] = useState(null);
    const [selectedChapterProject, setSelectedChapterProject] = useState(null);
    const [showChapterProject, setShowChapterProject] = useState(false);
    const [courseData, setCourseData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [progressRefreshTrigger, setProgressRefreshTrigger] = useState(0);
    const [isGenerationComplete, setIsGenerationComplete] = useState(true);
    const [expectedChapterCount, setExpectedChapterCount] = useState(0);
    const pollingIntervalRef = useRef(null);

    // Reset selected section and chapter project when course changes
    useEffect(() => {
        setSelectedSection(null);
        setHandsOnVisible(false);
        setShowChapterProject(false);
        setSelectedChapterProject(null);
        setCourseData(null);
        setError(null);
        setIsGenerationComplete(true);

        // Clear any existing polling interval
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, [currentCourse]);

    // Fetch course data when course is loaded
    useEffect(() => {
        const fetchCourseData = async () => {
            if (!currentCourse) {
                setCourseData(null);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const { course, chapters } = await getCourseWithContent(currentCourse.id);
                const data = { ...course, chapters };
                setCourseData(data);

                // Check if generation is complete
                const generationStatus = course.generation_status || 'complete';
                setIsGenerationComplete(generationStatus === 'complete');

                // Get expected chapter count from template if generation is in progress
                if (generationStatus !== 'complete' && course.template_id) {
                    try {
                        const { data: template } = await supabase
                            .from('course_templates')
                            .select('template_data')
                            .eq('id', course.template_id)
                            .single();

                        if (template?.template_data?.fullOutline) {
                            const expectedCount = template.template_data.fullOutline.length;
                            setExpectedChapterCount(expectedCount);
                        }
                    } catch (err) {
                        // Silently handle error
                    }
                }

                // Auto-select first section
                if (data.chapters && data.chapters.length > 0) {
                    const firstChapter = data.chapters[0];
                    if (firstChapter.sections && firstChapter.sections.length > 0) {
                        const firstSection = {
                            ...firstChapter.sections[0],
                            chapter_name: firstChapter.name,
                            chapter_id: firstChapter.id
                        };
                        setSelectedSection(firstSection);
                    }
                }
            } catch (err) {
                setError(err.message || 'Failed to load course content');
            } finally {
                setLoading(false);
            }
        };

        fetchCourseData();
    }, [currentCourse]);

    // Polling logic for background generation
    useEffect(() => {
        const checkGenerationStatus = async () => {
            if (!currentCourse || isGenerationComplete) {
                return;
            }

            try {
                // Query the course generation status AND fetch latest chapter data
                const { data, error } = await supabase
                    .from('courses')
                    .select('generation_status')
                    .eq('id', currentCourse.id)
                    .single();

                if (error) {
                    return;
                }

                // Always refresh course data to show newly generated chapters (live updates)
                const { course: refreshedCourse, chapters: refreshedChapters } = await getCourseWithContent(currentCourse.id);
                const refreshedData = { ...refreshedCourse, chapters: refreshedChapters };

                // Only update if chapter count changed (to avoid unnecessary re-renders)
                if (!courseData || refreshedChapters?.length !== courseData.chapters?.length) {
                    setCourseData(refreshedData);
                }

                // If generation is complete, stop polling
                if (data.generation_status === 'complete') {
                    setIsGenerationComplete(true);

                    // Clear polling interval
                    if (pollingIntervalRef.current) {
                        clearInterval(pollingIntervalRef.current);
                        pollingIntervalRef.current = null;
                    }

                    // Final refresh to ensure all data is loaded
                    const { course: finalCourse, chapters: finalChapters } = await getCourseWithContent(currentCourse.id);
                    const finalData = { ...finalCourse, chapters: finalChapters };
                    setCourseData(finalData);
                }
            } catch (err) {
                // Silently handle polling errors
            }
        };

        // Start polling if generation is not complete
        if (!isGenerationComplete && currentCourse) {
            // Initial check
            checkGenerationStatus();

            // Set up polling interval (every 5 seconds)
            pollingIntervalRef.current = setInterval(checkGenerationStatus, 5000);

            // Cleanup on unmount or when dependencies change
            return () => {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            };
        }
    }, [currentCourse, isGenerationComplete]);

    const toggleChapters = () => {
        if (chaptersState === 'hidden') {
            setChaptersState('entering');
            setTimeout(() => setChaptersState('visible'), 10);
            // Auto-refresh chapters when panel opens
            refreshChapters();
        } else if (chaptersState === 'visible') {
            setChaptersState('exiting');
            setTimeout(() => setChaptersState('hidden'), 300);
        }
    };

    const closeChapters = () => {
        if (chaptersState === 'visible') {
            setChaptersState('exiting');
            setTimeout(() => setChaptersState('hidden'), 300);
        }
    };

    const toggleHandsOn = () => {
        setHandsOnVisible(!handsOnVisible);
    };

    const handleCourseSelect = (course) => {
        onCourseSelect(course);
        // Close hands-on mode when switching courses
        setHandsOnVisible(false);
    };

    const handleSectionSelect = (section) => {
        setSelectedSection(section);
        setShowChapterProject(false);
        setSelectedChapterProject(null);
        // Close chapters panel after section selection on mobile
        if (window.innerWidth <= 768) {
            closeChapters();
        }
    };

    const handleChapterProjectSelect = (chapter) => {
        setSelectedChapterProject(chapter);
        setShowChapterProject(true);
        setHandsOnVisible(false);
        // Close chapters panel after project selection on mobile
        if (window.innerWidth <= 768) {
            closeChapters();
        }
    };

    const handleBackFromProject = () => {
        setShowChapterProject(false);
        setSelectedChapterProject(null);
        // Refresh progress to update chapter project completion status
        setProgressRefreshTrigger(prev => prev + 1);
    };

    const refreshChapters = async () => {
        if (!currentCourse) return;

        setLoading(true);
        setError(null);

        try {
            const { course, chapters } = await getCourseWithContent(currentCourse.id);
            const data = { ...course, chapters };
            setCourseData(data);

            // Refresh user progress as well
            setProgressRefreshTrigger(prev => prev + 1);
        } catch (err) {
            setError(err.message || 'Failed to refresh course content');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="learn-container">
            {/* Show banner when generation is not complete (but not for template courses from gallery) */}
            {!isGenerationComplete && !currentCourse?.is_from_template && <BackgroundGenerationBanner />}

            {/* Subtle status indicator in top right (but not for template courses from gallery) */}
            {!isGenerationComplete && !currentCourse?.is_from_template && (
                <GenerationStatusIndicator
                    isGenerating={!isGenerationComplete}
                    currentChapters={courseData?.chapters?.length || 0}
                    totalChapters={expectedChapterCount}
                />
            )}

            <div className="learn-header">
                <button className="chapters-toggle-btn" onClick={toggleChapters}>
                    <span className="chapters-toggle-text">Chapters</span>
                </button>
                {/* Course and section info */}
                {currentCourse && (
                    <div className="course-info-badge" style={{
                        padding: '8px 16px',
                        backgroundColor: 'var(--accent-light-blue)',
                        borderRadius: '4px',
                        fontSize: '14px',
                        marginLeft: '20px'
                    }}>
                        <strong>Course:</strong> {currentCourse.name}
                    </div>
                )}
                {selectedSection && (
                    <div className="section-info-badge" style={{
                        padding: '8px 16px',
                        backgroundColor: 'var(--accent-light-green)',
                        borderRadius: '4px',
                        fontSize: '14px',
                        border: '1px solid var(--accent-green)',
                        marginLeft: currentCourse ? '15px' : '20px'
                    }}>
                        <strong>Section:</strong> {selectedSection.name}
                    </div>
                )}
            </div>
            <Chapters
                chaptersState={chaptersState}
                onClose={closeChapters}
                currentCourse={currentCourse}
                currentUser={currentUser}
                isAuthenticated={isAuthenticated}
                onSectionSelect={handleSectionSelect}
                onChapterProjectSelect={handleChapterProjectSelect}
                selectedSection={selectedSection}
                onNavigateToInquiry={onNavigateToInquiry}
                onNavigateToLanding={onNavigateToLanding}
                courseData={courseData}
                loading={loading}
                error={error}
                progressRefreshTrigger={progressRefreshTrigger}
                onRefreshChapters={refreshChapters}
            />
            <div className={`learn-content ${handsOnVisible ? 'hands-on-visible' : 'hands-on-hidden'}`}>
                {loading ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px',
                        fontSize: '16px',
                        color: '#666'
                    }}>
                        Loading course content...
                    </div>
                ) : error ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px',
                        fontSize: '16px',
                        color: '#e74c3c'
                    }}>
                        {error}
                    </div>
                ) : showChapterProject ? (
                    <ChapterProject
                        chapter={selectedChapterProject}
                        currentUser={currentUser}
                        currentCourse={currentCourse}
                        onBack={handleBackFromProject}
                    />
                ) : (
                    <>
                        <CourseContent
                            onToggleHandsOn={toggleHandsOn}
                            handsOnVisible={handsOnVisible}
                            currentCourse={currentCourse}
                            isAuthenticated={isAuthenticated}
                            selectedSection={selectedSection}
                        />
                        {handsOnVisible && (
                            <CourseHandsOn
                                selectedSection={selectedSection}
                                currentUser={currentUser}
                                currentCourse={currentCourse}
                                courseData={courseData}
                                onSectionSelect={handleSectionSelect}
                                onCloseHandsOn={() => setHandsOnVisible(false)}
                                onProgressUpdate={() => setProgressRefreshTrigger(prev => prev + 1)}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default Learn;